import { useMemo } from "react";
import {
  Breadcrumb,
  DownloadExcel,
  ErrorAlert,
  HeartIcon,
  ImportData,
  Modal,
  ModalOpenBtn,
  PageHero,
  SearchToggle,
  TableRowsPerPage,
  useCanShow,
} from "@";
import { DataTable } from "@/shared/components/tanstackTable";
import { exportAllCharityOrderingData, getDonations } from "./utils/api";
import MultiSearch from "./components/MultiSearch";
import { useDonationsOperations } from "./utils/hooks";
import OrderNotesModal from "./components/OrderNotesModal";
import { excelHeaderCellsName } from "./utils/options";
import { TableActions, tableColumns } from "./components/TableOptions";
import { useDonationsListPage } from "./utils/useDonationsListPage";

const Index = () => {
  // Breadcrumb links
  const links = [{ name: "التبرعات", link: "" }];

  // Use the new hook with URL-based search and pagination
  const {
    data,
    setData,
    isLoading,
    error,
    pg,
    updatePg,
    search,
    updateSearch,
    handleSearchSubmit,
    handleSearchReset,
    refetch,
  } = useDonationsListPage();

  // Donation operations state and functions
  const {
    show,
    setPaid,
    modalData,
    noteChange,
    noteLoading,
    addOrderNotes,
    rejectBankTransfer,
    editBankTransferSuccess,
  } = useDonationsOperations(setData, refetch);

  // Table columns configuration with actions
  const columns = useMemo(
    () => [
      ...tableColumns,
      {
        id: "actions",
        header: "خيارات",
        size: 200,
        cell: ({ row }) => (
          <TableActions
            row={row.original}
            show={show}
            handelPaid={setPaid}
            rejectBankTransfer={rejectBankTransfer}
          />
        ),
      },
    ],
    [show, setPaid, rejectBankTransfer],
  );

  return (
    <>
      {/* Error alert */}
      <ErrorAlert reload={refetch} show={error} />

      {/* Breadcrumb */}
      <Breadcrumb links={links} />

      {/* Modal for donation details */}
      <ModalOpenBtn hidden />
      <Modal close={() => {}} size="max-w-7xl" title="تفاصيل التبرع">
        <OrderNotesModal
          modalData={modalData}
          noteChange={noteChange}
          noteLoading={noteLoading}
          addOrderNotes={addOrderNotes}
          editBankTransferSuccess={editBankTransferSuccess}
        />
      </Modal>

      <main className="table-card relative">
        <PageHero
          eyebrow="التبرعات"
          title="التبرعات"
          count={pg.rows}
          icon={HeartIcon}
          hideCount={isLoading}
        >
          <TableRowsPerPage
            pagination={pg}
            onRowsPerPageChange={(size) => updatePg({ size: size })}
          />

          <DownloadExcel
            title="تصدير التبرعات"
            fetch={getDonations}
            length={data?.length}
            search={search.values}
            headerCellsName={excelHeaderCellsName}
            logExport={{ action: 2, description: "تصدير التبرعات" }}
          />

          {useCanShow("permissions") && (
            <DownloadExcel
              title="تصدير كل طلبات الجمعية"
              headerCellsName={excelHeaderCellsName}
              fetch={exportAllCharityOrderingData}
              search={{}}
            />
          )}

          {/* Import data */}
          <ImportData success={refetch} to="order" />

          {/* Search toggle */}
          <SearchToggle toggle={() => updateSearch({ show: !search.show })} />
        </PageHero>

        {/* Multi Search inputs */}
        <MultiSearch
          search={search}
          onSubmit={handleSearchSubmit}
          updateSearch={updateSearch}
          onReset={handleSearchReset}
        />

        {/* Table */}
        <DataTable
          data={data}
          columns={columns}
          isLoading={isLoading}
          pagination={pg}
          onPageChange={(page) => updatePg({ page })}
          onPageSizeChange={(size) => updatePg({ size, page: 1 })}
        />
      </main>
    </>
  );
};

export default Index;
